import { makeAutoObservable } from "mobx"

import type {
  IAgingByStage,
  IBacklogBatchPreview,
  IContractPeriodMatrix,
  ICreateBacklogBatchInput,
  ICreateBacklogBatchResult,
  ICurrentPeriodStatus,
  IDuplicateNotifications,
  IFeeCalendarFilterInput,
  IFeeCalendarOutput,
  IFeeMatrixFilterInput,
  IFeeNotificationOverview,
  IFeeNotificationOverviewFilterInput,
  INotificationBacklogFilterInput,
  INotificationBacklogRow,
  ISendPreflight,
  IUnreachableContracts,
} from "@models/feeNotificationReport/feeNotificationReportModel"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import feeNotificationReportService from "@services/feeNotificationReport/feeNotificationReportService"

/**
 * State của các màn báo cáo Thông báo phí theo dự án.
 * Chỉ giữ observable + uỷ quyền http xuống service (không gọi http ở đây).
 */
class FeeNotificationReportStore {
  matrixData: IContractPeriodMatrix | undefined = undefined
  overviewData: IFeeNotificationOverview | undefined = undefined
  currentPeriodStatus: ICurrentPeriodStatus | undefined = undefined
  backlogData: PagedResultDto<INotificationBacklogRow> | undefined = undefined
  backlogPreview: IBacklogBatchPreview | undefined = undefined
  unreachableData: IUnreachableContracts | undefined = undefined
  agingByStageData: IAgingByStage | undefined = undefined
  duplicatesData: IDuplicateNotifications | undefined = undefined
  /** Preflight của đợt đang chuẩn bị gửi (Req 14) — `undefined` = chưa nạp. */
  sendPreflight: ISendPreflight | undefined = undefined
  /** Lịch thu phí của cửa sổ ngày đang xem — `undefined` = chưa nạp / chưa chọn dự án. */
  calendarData: IFeeCalendarOutput | undefined = undefined
  isLoading = false
  isLoadingOverview = false
  isLoadingCurrentPeriod = false
  isLoadingBacklog = false
  isLoadingPreview = false
  isCreatingBatch = false
  isLoadingUnreachable = false
  isLoadingAging = false
  isLoadingDuplicates = false
  isLoadingPreflight = false
  isLoadingCalendar = false
  isExporting = false

  constructor() {
    makeAutoObservable(this)
  }

  async getContractPeriodMatrix(params: IFeeMatrixFilterInput) {
    this.isLoading = true
    try {
      this.matrixData =
        await feeNotificationReportService.getContractPeriodMatrix(params)
      return this.matrixData
    } finally {
      this.isLoading = false
    }
  }

  async exportMatrix(params: IFeeMatrixFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportMatrix(params)
    } finally {
      this.isExporting = false
    }
  }

  initMatrix() {
    this.matrixData = undefined
  }

  /** Dashboard theo dự án (Req 3) — một request cho cả KPI và 4 khối. */
  async getFeeNotificationOverview(params: IFeeNotificationOverviewFilterInput) {
    this.isLoadingOverview = true
    try {
      this.overviewData =
        await feeNotificationReportService.getFeeNotificationOverview(params)
      return this.overviewData
    } finally {
      this.isLoadingOverview = false
    }
  }

  async exportOverview(params: IFeeNotificationOverviewFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportOverview(params)
    } finally {
      this.isExporting = false
    }
  }

  initOverview() {
    this.overviewData = undefined
  }

  /**
   * Dải trạng thái kỳ hiện tại (Req 12). Store KHÔNG tự quyết định có gọi hay không — component
   * gác bằng `shouldQueryCurrentPeriod()` để tôn trọng Req 12.5 ("chưa chọn dự án → không truy vấn").
   */
  async getCurrentPeriodStatus(projectId: number) {
    this.isLoadingCurrentPeriod = true
    try {
      this.currentPeriodStatus =
        await feeNotificationReportService.getCurrentPeriodStatus(projectId)
      return this.currentPeriodStatus
    } finally {
      this.isLoadingCurrentPeriod = false
    }
  }

  initCurrentPeriodStatus() {
    this.currentPeriodStatus = undefined
  }

  /* ────────────── Backlog chưa thông báo (Req 6) ────────────── */

  async getNotificationBacklog(params: INotificationBacklogFilterInput) {
    this.isLoadingBacklog = true
    try {
      this.backlogData =
        await feeNotificationReportService.getNotificationBacklog(params)
      return this.backlogData
    } finally {
      this.isLoadingBacklog = false
    }
  }

  /**
   * Xem trước lệnh tạo đợt — đọc-only (Req 6.5, 6.6, 6.8, 6.9).
   * Preview của client KHÔNG bao giờ được tin: server chạy lại chính phép tính này trong
   * `CreateBacklogBatch` và tự từ chối khi bị chặn / thiếu xác nhận nhiều kỳ.
   */
  async getBacklogBatchPreview(input: ICreateBacklogBatchInput) {
    this.isLoadingPreview = true
    try {
      this.backlogPreview =
        await feeNotificationReportService.getBacklogBatchPreview(input)
      return this.backlogPreview
    } finally {
      this.isLoadingPreview = false
    }
  }

  /** Đường ghi duy nhất của spec — luôn ra đợt `Status = NEW`, KHÔNG gửi (Req 6.4, 11.1). */
  async createBacklogBatch(
    input: ICreateBacklogBatchInput
  ): Promise<ICreateBacklogBatchResult> {
    this.isCreatingBatch = true
    try {
      return await feeNotificationReportService.createBacklogBatch(input)
    } finally {
      this.isCreatingBatch = false
    }
  }

  async exportBacklog(params: INotificationBacklogFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportBacklog(params)
    } finally {
      this.isExporting = false
    }
  }

  initBacklog() {
    this.backlogData = undefined
  }

  initBacklogPreview() {
    this.backlogPreview = undefined
  }

  /* ────────────── Hợp đồng không gửi được (Req 7) ────────────── */

  async getUnreachableContracts(params: IFeeMatrixFilterInput) {
    this.isLoadingUnreachable = true
    try {
      this.unreachableData =
        await feeNotificationReportService.getUnreachableContracts(params)
      return this.unreachableData
    } finally {
      this.isLoadingUnreachable = false
    }
  }

  async exportUnreachableContracts(params: IFeeMatrixFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportUnreachableContracts(params)
    } finally {
      this.isExporting = false
    }
  }

  initUnreachable() {
    this.unreachableData = undefined
  }

  /* ────────────── Aging × bậc đã nhắc (Req 8) ────────────── */

  async getAgingByReminderStage(params: IFeeMatrixFilterInput) {
    this.isLoadingAging = true
    try {
      this.agingByStageData =
        await feeNotificationReportService.getAgingByReminderStage(params)
      return this.agingByStageData
    } finally {
      this.isLoadingAging = false
    }
  }

  async exportAgingByReminderStage(params: IFeeMatrixFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportAgingByReminderStage(params)
    } finally {
      this.isExporting = false
    }
  }

  initAgingByStage() {
    this.agingByStageData = undefined
  }

  /* ────────────── Cảnh báo gửi trùng (Req 9) ────────────── */

  async getDuplicateNotifications(params: IFeeMatrixFilterInput) {
    this.isLoadingDuplicates = true
    try {
      this.duplicatesData =
        await feeNotificationReportService.getDuplicateNotifications(params)
      return this.duplicatesData
    } finally {
      this.isLoadingDuplicates = false
    }
  }

  async exportDuplicateNotifications(params: IFeeMatrixFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportDuplicateNotifications(params)
    } finally {
      this.isExporting = false
    }
  }

  initDuplicates() {
    this.duplicatesData = undefined
  }

  /* ────────────── Preflight trước khi gửi cả đợt (Req 14) ────────────── */

  /**
   * Nạp Preflight của một đợt — đọc-only. Store KHÔNG tự gửi: hành động gửi vẫn nằm ở
   * `FeeNotificationStore.sendAllNotification` và chỉ chạy sau khi người dùng xác nhận tường minh
   * trên modal (Req 14.1).
   */
  async getSendPreflight(batchId: number) {
    this.isLoadingPreflight = true
    try {
      this.sendPreflight = await feeNotificationReportService.getSendPreflight(
        batchId
      )
      return this.sendPreflight
    } finally {
      this.isLoadingPreflight = false
    }
  }

  initSendPreflight() {
    this.sendPreflight = undefined
  }

  /* ────────────── Lịch thu phí (design `fee-notification-calendar` §4.3) ────────────── */

  /**
   * Nạp lịch cho cửa sổ ngày đang xem. MỘT request trả cả `days` (aggregate) và `installments`
   * (chi tiết) nên bật/tắt toggle chế độ KHÔNG cần gọi lại — chỉ đổi cách đọc `calendarData`.
   * Store không tự gác `projectId`: màn gọi quyết định khi nào có quyền gọi (design §7.1).
   */
  async getFeeCalendar(params: IFeeCalendarFilterInput) {
    this.isLoadingCalendar = true
    try {
      this.calendarData = await feeNotificationReportService.getFeeCalendar(
        params
      )
      return this.calendarData
    } finally {
      this.isLoadingCalendar = false
    }
  }

  async exportFeeCalendar(params: IFeeCalendarFilterInput) {
    this.isExporting = true
    try {
      await feeNotificationReportService.exportFeeCalendar(params)
    } finally {
      this.isExporting = false
    }
  }

  initCalendar() {
    this.calendarData = undefined
  }
}

export default FeeNotificationReportStore
